import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from "@headlessui/react";
import { useUserActions } from "_actions";
import { cx } from "_lib/utils";
import { UserAtom } from "_states";
import { ChevronDown, LogOutIcon, MenuIcon, UserIcon, X } from "lucide-react";
import { Link, NavLink } from "react-router-dom";
import { useRecoilValue } from "recoil";

const navigation = [
  { name: "Holdings", href: "/" },
  { name: "Products", href: "/products" },
  { name: "Reports", href: "/reports" },
];
const userNavigation = [
  {
    name: "Your Profile",
    href: `/profile`,
    icon: UserIcon,
  },
  //   { name: "Settings", href: "/settings", icon: SettingsIcon },
];

export const Navbar = ({ gatewayInstance }: { gatewayInstance: any }) => {
  const user = useRecoilValue(UserAtom);
  const userActions = useUserActions();

  if (!user) return null;

  return (
    <Disclosure as="nav" className="bg-white shadow-sm fixed w-full z-10">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Link to="/">
                <img
                  alt="Capricorn Mindframe"
                  src={process.env.PUBLIC_URL + "/logo.svg"}
                  className="h-16 w-32"
                />
              </Link>
            </div>
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-4">
                {navigation.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    className={({ isActive }) =>
                      cx(
                        isActive ? "font-semibold !underline" : "",
                        "text-black rounded-md px-3 py-2 text-sm font-medium"
                      )
                    }
                  >
                    {item.name}
                  </NavLink>
                ))}
              </div>
            </div>
          </div>
          <div className="hidden md:block">
            <div className="ml-4 flex items-center md:ml-6">
              {/* Profile dropdown */}
              <Menu as="div" className="relative ml-3">
                <div>
                  <MenuButton className="relative flex max-w-xs items-center rounded-full text-sm font-medium">
                    <span className="absolute -inset-1.5" />
                    <span className="sr-only">Open user menu</span>
                    {user.name}
                    <ChevronDown className="h-5 w-5" />
                  </MenuButton>
                </div>
                <MenuItems
                  transition
                  className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                >
                  <MenuItem>
                    <a
                      onClick={() => {
                        gatewayInstance.openSmallplug();
                      }}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:outline-none"
                    >
                      <img
                        src={
                          process.env.PUBLIC_URL + "/icons/smallcase-icon.svg"
                        }
                        className="h-4 mx-1"
                      />
                      Smallcase UI
                    </a>
                  </MenuItem>
                  <hr />
                  {userNavigation.map((item) => (
                    <MenuItem key={item.name}>
                      <Link
                        to={item.href}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:outline-none"
                      >
                        <item.icon className="h-4" />
                        {item.name}
                      </Link>
                    </MenuItem>
                  ))}
                  <hr />
                  <MenuItem>
                    <a
                      onClick={userActions.logout}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:outline-none"
                    >
                      <LogOutIcon className="h-4" />
                      Sign out
                    </a>
                  </MenuItem>
                </MenuItems>
              </Menu>
            </div>
          </div>
          <div className="-mr-2 flex md:hidden">
            {/* Mobile menu button */}
            <DisclosureButton className="group relative inline-flex items-center justify-center rounded-md">
              <span className="absolute -inset-0.5" />
              <span className="sr-only">Open main menu</span>
              <MenuIcon
                aria-hidden="true"
                className="block h-6 w-6 group-data-[open]:hidden"
              />
              <X
                aria-hidden="true"
                className="hidden h-6 w-6 group-data-[open]:block"
              />
            </DisclosureButton>
          </div>
        </div>
      </div>

      <DisclosurePanel className="md:hidden">
        <div className="space-y-1 px-2 pb-3 pt-2 sm:px-3 text-sm">
          {navigation.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              className={({ isActive }) =>
                cx(
                  isActive ? "font-bold" : "hover:bg-gray-50",
                  "block rounded-md px-3 py-2 font-medium"
                )
              }
            >
              {item.name}
            </NavLink>
          ))}
        </div>
        <div className="border-t border-gray-700 pb-3 pt-4">
          <div className="flex items-center px-5">
            <div className="font-semibold">{user.name}</div>
            {/* <ChevronDown className="h-5 w-5" /> */}
          </div>
          <div className="mt-3 space-y-1 px-2">
            {userNavigation.map((item) => (
              <DisclosureButton
                key={item.name}
                as="a"
                href={item.href}
                className="block rounded-md px-3 py-2 text-sm font-medium"
              >
                {item.name}
              </DisclosureButton>
            ))}
            <hr />
            <DisclosureButton
              as="a"
              onClick={userActions.logout}
              className="block rounded-md px-3 py-2 text-sm font-medium"
            >
              Sign out
            </DisclosureButton>
          </div>
        </div>
      </DisclosurePanel>
    </Disclosure>
  );
};
